// Модалка создания доски с шаблонами по образцу kanban-newboard.jsx из прототипа.
// 4 пресета колонок + кастомный, опциональные дорожки.

import { useState } from 'react';
import { X, Plus, Trash2, GripVertical } from 'lucide-react';
import clsx from 'clsx';
import { api } from '../lib/api';

type ColumnKind = 'queue' | 'in-progress' | 'review' | 'done';

interface Template {
  id: string;
  label: string;
  description: string;
  columns: { title: string; kind: ColumnKind }[];
  lanes?: { title: string; accent: string }[];
}

const TEMPLATES: Template[] = [
  {
    id: 'kanban',
    label: 'Канбан',
    description: '4 колонки: Бэклог → В работе → Ревью → Готово',
    columns: [
      { title: 'Бэклог', kind: 'queue' },
      { title: 'В работе', kind: 'in-progress' },
      { title: 'Ревью', kind: 'review' },
      { title: 'Готово', kind: 'done' },
    ],
  },
  {
    id: 'scrum',
    label: 'Скрам',
    description: '5 колонок + дорожки по приоритету',
    columns: [
      { title: 'Бэклог', kind: 'queue' },
      { title: 'Спринт', kind: 'queue' },
      { title: 'В работе', kind: 'in-progress' },
      { title: 'Тест', kind: 'review' },
      { title: 'Готово', kind: 'done' },
    ],
    lanes: [
      { title: 'Срочное', accent: '#ef4444' },
      { title: 'Обычное', accent: '#3b82f6' },
    ],
  },
  {
    id: 'gtd',
    label: 'GTD',
    description: 'Inbox / Next / Waiting / Someday / Done',
    columns: [
      { title: 'Inbox', kind: 'queue' },
      { title: 'Next', kind: 'in-progress' },
      { title: 'Waiting', kind: 'review' },
      { title: 'Someday', kind: 'queue' },
      { title: 'Done', kind: 'done' },
    ],
  },
  {
    id: 'simple',
    label: 'Простой',
    description: '2 колонки: Открыто / Закрыто',
    columns: [
      { title: 'Открыто', kind: 'queue' },
      { title: 'Закрыто', kind: 'done' },
    ],
  },
];

interface Props {
  spaceId: string;
  onClose: () => void;
  onCreated?: (boardId: string) => void;
}

export function NewBoardModal({ spaceId, onClose, onCreated }: Props) {
  const [step, setStep] = useState<'template' | 'configure'>('template');
  const [templateId, setTemplateId] = useState<string>('kanban');
  const [title, setTitle] = useState('');
  const [columns, setColumns] = useState(TEMPLATES[0].columns);
  const [lanes, setLanes] = useState<{ title: string; accent: string }[]>([]);

  const utils = api.useUtils();
  const createBoardMutation = api.boards.create.useMutation();
  const createColumnMutation = api.columns.create.useMutation();
  const createLaneMutation = api.lanes.create.useMutation();
  const deleteColumnMutation = api.columns.delete.useMutation();
  const deleteDefaultLaneMutation = api.lanes.delete.useMutation();
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const selectTemplate = (id: string) => {
    const t = TEMPLATES.find((x) => x.id === id) ?? TEMPLATES[0];
    setTemplateId(id);
    setColumns(t.columns);
    setLanes(t.lanes ?? []);
  };

  const handleCreate = async () => {
    if (creating) return;
    setError(null);
    setCreating(true);
    const finalTitle = title.trim() || TEMPLATES.find((t) => t.id === templateId)?.label || 'Новая доска';
    let createdBoardId: string | null = null;
    try {
      const board = await createBoardMutation.mutateAsync({ spaceId, title: finalTitle });
      createdBoardId = board.id;

      // boards.create уже создаёт 4 default колонки и 1 lane — удалим их и поставим свои
      const fullData = await utils.boards.getFull.fetch({ boardId: board.id });
      for (const col of fullData.columns) {
        await deleteColumnMutation.mutateAsync({ id: col.id });
      }
      for (const lane of fullData.lanes) {
        await deleteDefaultLaneMutation.mutateAsync({ id: lane.id });
      }

      for (const col of columns) {
        await createColumnMutation.mutateAsync({ boardId: board.id, title: col.title, kind: col.kind });
      }

      if (lanes.length === 0) {
        await createLaneMutation.mutateAsync({ boardId: board.id, title: 'Основная' });
      } else {
        for (const lane of lanes) {
          await createLaneMutation.mutateAsync({ boardId: board.id, title: lane.title, accent: lane.accent });
        }
      }

      utils.spaces.getWithBoards.invalidate({ spaceId });
      onCreated?.(board.id);
      onClose();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('NewBoardModal create failed', err);
      const msg = err instanceof Error ? err.message : 'Неизвестная ошибка';
      setError(
        createdBoardId
          ? `Доска создана, но не удалось добавить колонки/дорожки: ${msg}. Откройте доску и доработайте вручную.`
          : `Не удалось создать доску: ${msg}`,
      );
      // Если доска была создана — пусть пользователь её увидит после закрытия
      if (createdBoardId) {
        utils.spaces.getWithBoards.invalidate({ spaceId });
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-pop w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-line-light dark:border-line-dark">
          <h2 className="text-base font-semibold">
            {step === 'template' ? 'Выбери шаблон' : 'Настрой доску'}
          </h2>
          <button onClick={onClose} className="opacity-60 hover:opacity-100">
            <X size={18} />
          </button>
        </header>

        {step === 'template' && (
          <div className="p-5 flex-1 overflow-auto">
            <div className="grid grid-cols-2 gap-3">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => selectTemplate(t.id)}
                  className={clsx(
                    'text-left rounded-lg border p-4 transition-all',
                    templateId === t.id
                      ? 'border-accent bg-accent/5 shadow-board-hover'
                      : 'border-line-light dark:border-line-dark hover:border-accent/50',
                  )}
                >
                  <div className="font-semibold text-sm mb-1">{t.label}</div>
                  <div className="text-xs text-ink-faint_l dark:text-ink-faint_d mb-3">{t.description}</div>
                  <div className="flex gap-1 flex-wrap">
                    {t.columns.map((c) => (
                      <span
                        key={c.title}
                        className="inline-block px-1.5 py-0.5 text-[10px] rounded bg-surface2-light dark:bg-surface2-dark"
                      >
                        {c.title}
                      </span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 'configure' && (
          <div className="p-5 flex-1 overflow-auto space-y-5">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-ink-muted_l dark:text-ink-muted_d mb-1.5">
                Название доски
              </label>
              <input
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={TEMPLATES.find((t) => t.id === templateId)?.label}
                className="w-full px-3 py-2 text-sm rounded border border-line-light dark:border-line-dark bg-surface2-light dark:bg-surface2-dark"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted_l dark:text-ink-muted_d">
                  Колонки ({columns.length})
                </h3>
                <button
                  type="button"
                  onClick={() => setColumns([...columns, { title: 'Новая', kind: 'queue' }])}
                  className="text-xs text-accent flex items-center gap-1"
                >
                  <Plus size={12} /> Колонка
                </button>
              </div>
              <ul className="space-y-1.5">
                {columns.map((c, idx) => (
                  <li key={idx} className="flex items-center gap-2 px-2 py-1 rounded bg-surface2-light dark:bg-surface2-dark">
                    <GripVertical size={12} className="text-ink-faint_l dark:text-ink-faint_d" />
                    <input
                      value={c.title}
                      onChange={(e) => {
                        const next = [...columns];
                        next[idx] = { ...c, title: e.target.value };
                        setColumns(next);
                      }}
                      className="flex-1 bg-transparent text-sm outline-none"
                    />
                    <select
                      value={c.kind}
                      onChange={(e) => {
                        const next = [...columns];
                        next[idx] = { ...c, kind: e.target.value as ColumnKind };
                        setColumns(next);
                      }}
                      className="text-xs bg-transparent border border-line-light dark:border-line-dark rounded px-1 py-0.5"
                    >
                      <option value="queue">queue</option>
                      <option value="in-progress">in-progress</option>
                      <option value="review">review</option>
                      <option value="done">done</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => setColumns(columns.filter((_, i) => i !== idx))}
                      className="opacity-50 hover:opacity-100 hover:text-red-500"
                    >
                      <Trash2 size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted_l dark:text-ink-muted_d">
                  Дорожки ({lanes.length})
                </h3>
                <button
                  type="button"
                  onClick={() => setLanes([...lanes, { title: 'Новая', accent: '#3b82f6' }])}
                  className="text-xs text-accent flex items-center gap-1"
                >
                  <Plus size={12} /> Дорожка
                </button>
              </div>
              <ul className="space-y-1.5">
                {lanes.map((l, idx) => (
                  <li key={idx} className="flex items-center gap-2 px-2 py-1 rounded bg-surface2-light dark:bg-surface2-dark">
                    <input
                      type="color"
                      value={l.accent}
                      onChange={(e) => {
                        const next = [...lanes];
                        next[idx] = { ...l, accent: e.target.value };
                        setLanes(next);
                      }}
                      className="w-5 h-5 rounded cursor-pointer"
                    />
                    <input
                      value={l.title}
                      onChange={(e) => {
                        const next = [...lanes];
                        next[idx] = { ...l, title: e.target.value };
                        setLanes(next);
                      }}
                      className="flex-1 bg-transparent text-sm outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setLanes(lanes.filter((_, i) => i !== idx))}
                      className="opacity-50 hover:opacity-100 hover:text-red-500"
                    >
                      <Trash2 size={12} />
                    </button>
                  </li>
                ))}
                {lanes.length === 0 && (
                  <p className="text-xs text-ink-faint_l dark:text-ink-faint_d">
                    Без дорожек — классический одномерный канбан. Добавь чтобы получить 2D-сетку.
                  </p>
                )}
              </ul>
            </div>
          </div>
        )}

        {error && (
          <div className="mx-5 mb-2 px-3 py-2 text-xs rounded bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/30">
            {error}
          </div>
        )}
        <footer className="flex items-center justify-between gap-2 px-5 py-3 border-t border-line-light dark:border-line-dark bg-surface2-light dark:bg-surface2-dark">
          {step === 'template' ? (
            <>
              <button onClick={onClose} className="px-3 py-1.5 text-sm text-ink-faint_l hover:text-ink-light dark:hover:text-ink-dark">
                Отмена
              </button>
              <button
                onClick={() => setStep('configure')}
                className="px-4 py-1.5 text-sm rounded bg-accent text-white"
              >
                Дальше
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setStep('template')} className="px-3 py-1.5 text-sm text-ink-faint_l hover:text-ink-light dark:hover:text-ink-dark">
                ← Назад
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="px-4 py-1.5 text-sm rounded bg-accent text-white disabled:opacity-50"
              >
                {creating ? 'Создаём…' : 'Создать доску'}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
